import React, { useEffect } from 'react';
import { ViewerContext } from '../../providers/ViewerContextProvider';
import { ContentSection } from '../../components/Layout/ContentSection';
import i18n from '@/utils/i18n';
import { CustomTable } from '../../components/Table';
import { useAllItemTypesQuery } from '../../graphql/queries/allItemTypes.generated';
import CustomPagination from '../../components/CustomPagination';
import ItemTypeDetailModal from './ItemTypeDetailModal';
import {
  itemTypeCategories,
  ItemTypeCategoriesWithAll,
  ItemTypeFormState,
} from './types';
import { Button } from 'reactstrap';
import { FaPlus } from 'react-icons/fa';
import { DEFAULT_TRACKABLE_ITEM_TYPE_ICON } from '@/utils/imageUrl';
import { MainHeader } from '../../components/Layout/MainHeader';
import { Role } from '../../contants';
import { ItemTypeTableRow } from './ItemTypeTableRow';

const archivedStatus = ['ARCHIVED', 'NOT_ARCHIVED', 'ALL'] as const;
type ArchivedStatus = (typeof archivedStatus)[number];

const PAGE_SIZE = 20;

export default function EditItemType() {
  const viewer = React.useContext(ViewerContext);
  const isAdministrator = viewer.role === Role.administrator;

  const defaultItemType: ItemTypeFormState = {
    id: 0,
    name: '',
    color_hex_code: '',
    category: '',
    epc_asset_id: 0,
    image_url: DEFAULT_TRACKABLE_ITEM_TYPE_ICON,
    archived: false,
    weight: 0,
  };

  const [itemType, setItemType] =
    React.useState<ItemTypeFormState>(defaultItemType);
  const [category, setCatgeory] =
    React.useState<ItemTypeCategoriesWithAll>('ALL');
  const [mode, setMode] = React.useState<'update' | 'create'>('create');
  const [detailModelOpen, setDetailModalOpen] = React.useState(false);
  const [archivedFilter, setShowArchivedFilter] =
    React.useState<ArchivedStatus>('NOT_ARCHIVED');
  const [currentPage, setCurrentPage] = React.useState(1);

  const { data, loading, error, refetch } = useAllItemTypesQuery({
    variables: {
      filter: {
        category: category === 'ALL' ? undefined : category,
        archived:
          archivedFilter === 'ALL' ? undefined : archivedFilter === 'ARCHIVED',
      },
      skip: (currentPage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    // Reset to the first page when category or showArchived changes
    setCurrentPage(1);
  }, [category, archivedFilter]);

  useEffect(() => {
    // refetch data when currentPage changes
    refetch({
      skip: (currentPage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    });
  }, [currentPage, refetch]);

  return (
    <div>
      <MainHeader title={i18n.t('ItemType:title')} />
      <ContentSection title={i18n.t('ItemType:filter')} collapsable>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('ItemType:category')}
          </span>
          <select
            className="form-control custom-select"
            onChange={(e) =>
              setCatgeory(e.target.value as ItemTypeCategoriesWithAll)
            }
            value={category}
          >
            {itemTypeCategories.map((c) => (
              <option
                key={c}
                value={c}
                label={i18n.t(`ItemType:category${c}`)}
              />
            ))}
          </select>
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('ItemType:archived')}
          </span>
          <select
            className="form-control custom-select"
            onChange={(e) =>
              setShowArchivedFilter(e.target.value as ArchivedStatus)
            }
            value={archivedFilter}
          >
            {archivedStatus.map((status) => (
              <option
                key={status}
                value={status}
                label={i18n.t(`ItemType:archivedStatus${status}`)}
              />
            ))}
          </select>
        </div>
      </ContentSection>
      <ContentSection title={i18n.t('ItemType:itemTypes')} collapsable>
        <Button
          className="float-end mb-3"
          color="primary"
          onClick={() => {
            setItemType(defaultItemType);
            setMode('create');
            setDetailModalOpen(true);
          }}
          disabled={!isAdministrator}
        >
          <FaPlus className="me-1" />
          {i18n.t('ItemType:create')}
        </Button>
        <CustomTable
          headers={[
            i18n.t('Common:id'),
            i18n.t('Common:image'),
            i18n.t('Common:name'),
            i18n.t('ItemType:sku'),
            i18n.t('ItemType:colorHexCode'),
            i18n.t('ItemType:category'),
            i18n.t('ItemType:billingCategory'),
            i18n.t('ItemType:price'),
            i18n.t('ItemType:billingFrequency'),
            i18n.t('ItemType:archived'),
            '',
          ]}
          hover
          striped
          bordered
          loading={loading}
          error={error}
          rows={(data?.itemTypes.items || []).map((itemType) => (
            <ItemTypeTableRow
              key={`item-type-${itemType.id}`}
              itemType={itemType}
              onEdit={() => {
                setItemType(itemType);
                setMode('update');
                setDetailModalOpen(true);
              }}
            />
          ))}
        />
        <CustomPagination
          currentPage={currentPage}
          pageCount={Math.ceil((data?.itemTypes.total || 0) / PAGE_SIZE)}
          onPageChange={(page) => setCurrentPage(page)}
        />
        <ItemTypeDetailModal
          itemType={itemType}
          setItemType={setItemType}
          mode={mode}
          open={detailModelOpen}
          setOpen={setDetailModalOpen}
        />
      </ContentSection>
    </div>
  );
}
